import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, ShieldOff, ShieldCheck, Globe, Laptop, History, KeyRound, Copy, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { SACard, timeAgo } from "@/components/superadmin/SAPrimitives";

/**
 * SuperAdminSecurity — list every active superadmin session across all
 * devices, with a one-click revoke per row. Revoking the session you're
 * currently using signs *this* tab out and bounces to /superadmin/login.
 */
export default function SuperAdminSecurity() {
    const [rows, setRows] = useState([]);
    const [currentSid, setCurrentSid] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [revoking, setRevoking] = useState(null);
    const [confirmSid, setConfirmSid] = useState(null);
    const [includeRevoked, setIncludeRevoked] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/superadmin/sessions", {
                params: { include_revoked: includeRevoked },
            });
            setRows(data.sessions || []);
            setCurrentSid(data.current_sid || null);
            setErr("");
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [includeRevoked]);

    useEffect(() => {
        load();
        // Poll every 20s so "last seen" stays meaningful without a
        // dedicated WS channel — Security is a rarely-open panel.
        const iv = setInterval(load, 20000);
        return () => clearInterval(iv);
    }, [load]);

    const doRevoke = async (sid) => {
        setRevoking(sid);
        try {
            await http.post(`/superadmin/sessions/${sid}/revoke`);
            toast.success("Session revoked");
            if (sid === currentSid) {
                // Revoking our own token — the next call will 401, just
                // go to the isolated SA login directly to skip the flash.
                setTimeout(() => {
                    window.location.assign("/superadmin/login");
                }, 600);
                return;
            }
            load();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setRevoking(null);
            setConfirmSid(null);
        }
    };

    const active = rows.filter((r) => !r.revoked);

    return (
        <div className="space-y-5" data-testid="sa-security-page">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Security</h1>
                    <div className="mt-1 text-[12px] text-white/55">
                        {active.length} active superadmin session{active.length === 1 ? "" : "s"} ·
                        Revoke any device you don&apos;t recognise.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="flex h-8 items-center gap-1.5 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[11.5px] text-white/65">
                        <input
                            type="checkbox"
                            data-testid="sa-security-include-revoked"
                            checked={includeRevoked}
                            onChange={(e) => setIncludeRevoked(e.target.checked)}
                            className="accent-[#00C2FF]"
                        />
                        Show revoked
                    </label>
                    <button
                        onClick={load}
                        data-testid="sa-security-refresh"
                        className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                    >
                        <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                    </button>
                </div>
            </div>

            {err && (
                <SACard className="text-red-300" data-testid="sa-security-error">
                    {err}
                </SACard>
            )}

            <SigningKeyRotationPanel />

            <SACard className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-b border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                                <th className="px-5 py-2.5 font-medium">Account</th>
                                <th className="px-2 py-2.5 font-medium">Device</th>
                                <th className="px-2 py-2.5 font-medium">IP</th>
                                <th className="px-2 py-2.5 font-medium">Signed in</th>
                                <th className="px-2 py-2.5 font-medium">Last seen</th>
                                <th className="px-2 py-2.5 font-medium">Status</th>
                                <th className="px-5 py-2.5 text-right font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((s) => {
                                const isMe = s.id === currentSid;
                                return (
                                    <tr
                                        key={s.id}
                                        className="border-b border-white/5 transition-colors hover:bg-white/[0.03]"
                                        data-testid={`sa-security-row-${s.id}`}
                                    >
                                        <td className="px-5 py-3">
                                            <div className="font-semibold text-white">{s.email}</div>
                                            <div className="mt-0.5 font-mono text-[10.5px] text-white/40">
                                                {s.id.slice(0, 8)}…
                                            </div>
                                        </td>
                                        <td className="px-2 py-3 text-white/80">
                                            <div className="flex items-center gap-1.5">
                                                <Laptop size={11} className="text-white/50" />
                                                {s.device_label}
                                            </div>
                                        </td>
                                        <td className="px-2 py-3 font-mono text-[11px] text-white/70">
                                            <div className="flex items-center gap-1.5">
                                                <Globe size={11} className="text-white/50" />
                                                {s.ip || "—"}
                                            </div>
                                        </td>
                                        <td className="px-2 py-3 text-white/65">{timeAgo(s.created_at)}</td>
                                        <td className="px-2 py-3 text-white/65">
                                            <div className="flex items-center gap-1.5">
                                                <History size={11} className="text-white/50" />
                                                {timeAgo(s.last_seen_at)}
                                            </div>
                                        </td>
                                        <td className="px-2 py-3">
                                            {s.revoked ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-[10.5px] font-semibold text-red-300">
                                                    <ShieldOff size={10} /> revoked
                                                </span>
                                            ) : isMe ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-[#00C2FF]/15 px-2 py-0.5 text-[10.5px] font-semibold text-[#00C2FF]">
                                                    <ShieldCheck size={10} /> this device
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-300">
                                                    <ShieldCheck size={10} /> active
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            {!s.revoked && (
                                                <button
                                                    onClick={() => setConfirmSid(s.id)}
                                                    disabled={revoking === s.id}
                                                    data-testid={`sa-security-revoke-${s.id}`}
                                                    className="inline-flex items-center gap-1 rounded-md border border-red-400/30 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold text-red-300 transition-colors hover:border-red-400/70 hover:bg-red-500/20 disabled:opacity-50"
                                                >
                                                    <ShieldOff size={11} />
                                                    {revoking === s.id ? "Revoking…" : isMe ? "Revoke (sign out)" : "Revoke"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-5 py-12 text-center text-white/40">
                                        No superadmin sessions to show.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SACard>

            {confirmSid && (
                <ConfirmRevokeModal
                    session={rows.find((r) => r.id === confirmSid)}
                    isMe={confirmSid === currentSid}
                    busy={revoking === confirmSid}
                    onClose={() => setConfirmSid(null)}
                    onConfirm={() => doRevoke(confirmSid)}
                />
            )}
        </div>
    );
}

function ConfirmRevokeModal({ session, isMe, busy, onClose, onConfirm }) {
    if (!session) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            role="dialog"
            data-testid="sa-security-confirm-modal"
        >
            <div className="w-full max-w-md rounded-lg border border-white/10 bg-[#0a142b] p-5 text-white shadow-xl">
                <div className="flex items-center gap-2 text-[13px] font-semibold">
                    <ShieldOff size={14} className="text-red-300" /> Revoke this session?
                </div>
                <div className="mt-3 space-y-1.5 rounded-md border border-white/5 bg-white/[0.02] p-3 text-[12px]">
                    <Row k="Account" v={session.email} />
                    <Row k="Device" v={session.device_label} />
                    <Row k="IP" v={session.ip || "—"} />
                    <Row k="Last seen" v={new Date(session.last_seen_at).toLocaleString()} />
                </div>
                {isMe ? (
                    <p className="mt-3 text-[12px] text-amber-300">
                        You&apos;re revoking <b>this device</b>. You&apos;ll be signed out
                        immediately and bounced to the Super Admin login.
                    </p>
                ) : (
                    <p className="mt-3 text-[12px] text-white/65">
                        The next request from that device will return 401 and the user will
                        need to sign in again.
                    </p>
                )}
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        data-testid="sa-security-confirm-cancel"
                        className="rounded-md border border-white/10 px-3 py-1.5 text-[12px] text-white/75 hover:bg-white/5"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        data-testid="sa-security-confirm-revoke"
                        className="rounded-md bg-red-500/90 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-red-500 disabled:opacity-60"
                    >
                        {busy ? "Revoking…" : "Revoke now"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <span className="text-[10.5px] uppercase tracking-wider text-white/45">{k}</span>
            <span className="truncate text-white/85">{v}</span>
        </div>
    );
}

/**
 * SigningKeyRotationPanel — generates a fresh Ed25519 key pair for the
 * Desktop entitlement-signing key (backend/services/entitlement_signing.py).
 * Deliberately NOT a one-click "rotate now" action: the backend has no
 * safe way to rewrite its own Railway env vars, so this only ever
 * generates and displays the values once for an operator to set manually
 * -- the new private key is never persisted anywhere by this UI, only
 * shown for a one-time copy.
 */
function SigningKeyRotationPanel() {
    const [open, setOpen] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [preview, setPreview] = useState(null);
    const [copied, setCopied] = useState(null);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await http.post("/superadmin/licensing/signing-key/rotate-preview");
            setPreview(data);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setGenerating(false);
        }
    };

    const copy = (label, value) => {
        navigator.clipboard.writeText(value);
        setCopied(label);
        setTimeout(() => setCopied(null), 1500);
    };

    return (
        <SACard data-testid="sa-signing-key-panel">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[12px] font-semibold text-white/80">
                    <KeyRound size={14} /> Entitlement signing key
                </div>
                <button
                    onClick={() => setOpen((v) => !v)}
                    data-testid="sa-signing-key-toggle"
                    className="text-[11px] text-white/55 hover:text-white/85"
                >
                    {open ? "Hide" : "Rotate key"}
                </button>
            </div>
            <p className="mt-1 text-[11px] text-white/55">
                Signs the entitlement every activated Desktop verifies offline. Rotating requires
                a manual, two-step rollout — this only generates the values, nothing is changed until
                you set them yourself.
            </p>

            {open && (
                <div className="mt-3 space-y-3 border-t border-white/5 pt-3">
                    {!preview ? (
                        <button
                            onClick={generate}
                            disabled={generating}
                            data-testid="sa-signing-key-generate"
                            className="inline-flex items-center gap-1.5 rounded-md bg-blue-500 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-blue-600 disabled:opacity-50"
                        >
                            <KeyRound size={12} /> {generating ? "Generating…" : "Generate new key pair"}
                        </button>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 rounded-md border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-[11.5px] text-amber-200">
                                <AlertTriangle size={13} className="shrink-0" />
                                Shown once. Copy these now — closing this panel does not let you retrieve
                                the private key again (generate a new one instead).
                            </div>
                            <KeyField
                                label="1. Backend: set as LICENSE_SIGNING_PRIVATE_KEY_B64"
                                value={preview.new_private_key_b64}
                                sensitive
                                copied={copied === "priv"}
                                onCopy={() => copy("priv", preview.new_private_key_b64)}
                            />
                            <KeyField
                                label="2. Backend: set as LICENSE_SIGNING_PREVIOUS_PUBLIC_KEY_B64"
                                value={preview.previous_public_key_b64}
                                copied={copied === "prevpub"}
                                onCopy={() => copy("prevpub", preview.previous_public_key_b64)}
                            />
                            <KeyField
                                label="3. Next Desktop release: PUBLIC_KEY_B64 in desktop/licensing.js"
                                value={preview.new_public_key_b64}
                                copied={copied === "newpub"}
                                onCopy={() => copy("newpub", preview.new_public_key_b64)}
                            />
                            <p className="text-[11px] text-white/50">
                                Redeploy the backend after step 1–2. Entitlements already issued under the
                                outgoing key keep verifying (server- and Desktop-side, once that release
                                also sets PREVIOUS_PUBLIC_KEY_B64) until they expire on their own — no
                                forced mass re-activation.
                            </p>
                        </>
                    )}
                </div>
            )}
        </SACard>
    );
}

function KeyField({ label, value, sensitive, copied, onCopy }) {
    return (
        <div>
            <div className="mb-1 text-[10.5px] uppercase tracking-wider text-white/45">{label}</div>
            <div className="flex items-center gap-2">
                <code className="flex-1 truncate rounded border border-white/10 bg-black/30 px-2 py-1.5 font-mono text-[11px] text-white/80">
                    {value}
                </code>
                <button
                    onClick={onCopy}
                    data-testid={`sa-signing-key-copy-${sensitive ? "private" : "public"}`}
                    className="shrink-0 rounded border border-white/10 p-1.5 text-white/65 hover:bg-white/5"
                >
                    {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                </button>
            </div>
        </div>
    );
}
